/**
 * Job Analysis Service
 * Handles job posting skills analysis operations
 */
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { createErrorLogger, ErrorType } from '../utils/logger';

const logger = createErrorLogger('JobAnalyzerService');

const JobAnalyzerService = {
  /**
   * Process a job posting to extract skills
   * @param {string} jobText
   * @param {File[]} files
   * @param {string} userId - The user ID to associate with this analysis
   * @returns {Promise<{ skills: Array, userId: string }>}
   */
  processJobPosting: async (jobText, files = [], userId) => {
    logger.info('processJobPosting', `processJobPosting called for user: ${userId}`, { userId });
    const formData = new FormData();
    formData.append('job_text', jobText || '');
    files.forEach(file => formData.append('files', file));
    formData.append('user_id', userId);
    const response = await axios.post(
      `${API_BASE_URL}/job/analyze`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const { result } = response.data;
    if (!result || !Array.isArray(result.skills)) {
      throw new Error('Job analysis returned an invalid payload.');
    }
    // Map backend skill objects to front-end expected fields: name, category, importance, context, matching
    const skills = result.skills.map(item => ({
      name: item.skill || item.name,
      category: item.category,
      importance: item.level || item.importance,
      context: item.context,
      matching: item.matching // Include matching data from CV-Job analysis
    }));

    return { ...result, skills, userId: result.userId || userId };
  },

  /**
   * Load job analysis data directly from disk
   * @param {string} userId - The user ID to load data for
   * @returns {Promise<{ skills: Array, userId: string }>}
   */
  loadJobData: async (userId) => {
    logger.info('loadJobData', `Loading job data from disk for user: ${userId}`, { userId });
    logger.info('loadJobData', `API URL: ${API_BASE_URL}/job/load/${userId}`, { url: `${API_BASE_URL}/job/load/${userId}` });

    try {
      logger.info('loadJobData', 'Making API request to load job data...', {});
      const response = await axios.get(
        `${API_BASE_URL}/job/load/${userId}`
      );

      logger.info('loadJobData', 'Job data loaded from disk', { dataSize: response.data?.result?.length || 0 });
      const { result } = response.data;

      if (!result) {
        logger.info('loadJobData', 'No job data found, returning empty skills', {});
        return { skills: [], userId };
      }

      // Map backend skill objects to front-end expected fields: name, category, importance, context
      const backendSkills = Array.isArray(result) ? result : (Array.isArray(result.skills) ? result.skills : []);
      const skills = backendSkills.map(item => ({
        name: item.skill || item.name,
        category: item.category,
        importance: item.level || item.importance,
        context: item.context,
        matching: item.matching
      }));

      logger.info('loadJobData', 'Returning job data', { skillCount: skills.length });
      return { ...result, skills, userId: result.userId || userId };
    } catch (error) {
      logger.error('loadJobData', 'Error loading job data from disk', error, ErrorType.API_CALL);
      logger.error('loadJobData', 'Error details', { message: error.message }, ErrorType.API_CALL);
      if (error.response) {
        logger.error('loadJobData', 'Error response', { status: error.response.status, data: error.response.data }, ErrorType.API_CALL);
      }
      throw error;
    }
  },

  /**
   * Load company research data to check if it exists
   * This is used to determine if company research is available for training recommendations
   * @param {string} userId - The user ID (not used for company data, but kept for API compatibility)
   * @returns {Promise<{ companyName: string, report: string, sections: Array }>}
   */
  loadCompanyData: async (userId) => {
    logger.info('loadCompanyData', `Checking company research data availability for user: ${userId}`, { userId });

    try {
      // Company research is stored globally (admin), not per user
      // Load the latest company research data
      const response = await axios.get(`${API_BASE_URL}/company/load`);

      logger.info('loadCompanyData', 'Company research data loaded', { hasResult: !!response.data?.result });
      const { result } = response.data;

      if (!result) {
        logger.info('loadCompanyData', 'No company research found', {});
        return null;
      }

      // Extract company data from result
      const companyName = result.companyName || '';
      const report = result.report || '';
      const sections = result.sections || [];

      logger.info('loadCompanyData', 'Returning company research data', {
        companyName,
        reportLength: report.length,
        sectionCount: sections.length
      });

      return { companyName, report, sections };
    } catch (error) {
      logger.error('loadCompanyData', 'Error loading company research data', error, ErrorType.API_CALL);
      if (error.response?.status === 404) {
        logger.info('loadCompanyData', 'Company research not found');
        return null;
      }
      throw error;
    }
  },

  /**
   * Get job categories from database
   * @returns {Promise<Array<string>>}
   */
  getCategories: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/system/categories/names?category_type=job&active_only=true`);
      const { result } = response.data;
      logger.info('getCategories', 'Job categories loaded from database', { count: result?.length || 0 });
      return result || [];
    } catch (error) {
      logger.error('getCategories', 'Error loading job categories', error, ErrorType.API_CALL);
      throw error;
    }
  },

  /**
   * Regenerate CV-job matching percentages for existing job skills
   * @param {string} userId - The user ID to regenerate matching for
   * @returns {Promise<{ skills: Array, userId: string }>}
   */
  regenerateMatching: async (userId) => {
    logger.info('regenerateMatching', `Regenerating CV-job matching for user: ${userId}`, { userId });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/job/regenerate-matching`,
        { user_id: userId },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { result } = response.data;
      logger.info('regenerateMatching', 'CV-job matching regenerated successfully', { skillCount: result?.skills?.length || 0 });

      // Map backend skill objects to front-end expected fields with updated matching data
      const skills = (result.skills || []).map(item => ({
        name: item.skill || item.name,
        category: item.category,
        importance: item.level || item.importance,
        context: item.context,
        matching: item.matching // Updated matching data from CV-Job analysis
      }));

      return { result: { skills }, userId };
    } catch (error) {
      logger.error('regenerateMatching', 'Error regenerating CV-job matching', error, ErrorType.API_CALL);
      throw error;
    }
  }
};

export default JobAnalyzerService;
